import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LogOut, ShieldCheck, Briefcase, ChevronDown, Zap, FileText, FolderOpen, Building2, Loader2, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useViewRole } from "../../context/ViewRoleContext";
import QuickAddDialog from "../quickadd/QuickAddDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";

export default function Topbar() {
  const { user, logout } = useAuth();
  const { viewAs, setViewAs, isAdmin, effectiveRole } = useViewRole();
  const navigate = useNavigate();
  const [quickOpen, setQuickOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalResults, setGlobalResults] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const searchRef = useRef(null);
  const searchRequestRef = useRef(0);

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");

  useEffect(() => {
    const q = globalSearch.trim();

    if (q.length < 2) {
      setGlobalResults(null);
      setGlobalLoading(false);
      return;
    }

    const requestId = ++searchRequestRef.current;
    setGlobalLoading(true);

    const timer = setTimeout(async () => {
      try {
        const { data } = await import("../../lib/api").then((m) =>
          m.default.get(`/search/global?q=${encodeURIComponent(q)}`)
        );

        if (requestId === searchRequestRef.current) {
          setGlobalResults(data || { clients: [], dossiers: [], devis: [] });
        }
      } catch {
        if (requestId === searchRequestRef.current) {
          setGlobalResults({ clients: [], dossiers: [], devis: [] });
        }
      } finally {
        if (requestId === searchRequestRef.current) {
          setGlobalLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [globalSearch]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setGlobalSearch("");
        setGlobalResults(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const closeGlobalSearch = () => {
    setGlobalSearch("");
    setGlobalResults(null);
  };

  const openGlobalClient = (client) => {
    closeGlobalSearch();
    navigate("/clients");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("crm:open-client", {
        detail: { clientId: client.id },
      }));
    }, 80);
  };

  const openGlobalDevis = (devis) => {
    closeGlobalSearch();
    navigate(`/devis?open=${encodeURIComponent(devis.id)}`);
  };

  const openGlobalDossier = (dossier) => {
    closeGlobalSearch();
    const targetDevis = (globalResults?.devis || []).find(
      (d) => d.dossier_id === dossier.id
    );

    if (targetDevis?.id) {
      navigate(`/devis?open=${encodeURIComponent(targetDevis.id)}`);
    } else {
      navigate("/devis");
    }
  };

  return (
    <header className="h-[72px] sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-slate-200 flex items-center gap-4 px-4 sm:px-6">
      <div className="relative flex-1 max-w-[720px] hidden sm:block" ref={searchRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input
          data-testid="global-search-input"
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          placeholder="Rechercher un client, devis, chantier…"
          className="w-full h-10 pl-10 pr-10 rounded-lg bg-white border border-slate-200 text-sm text-[#102A4C] placeholder:text-slate-600 focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-50"
        />

        {globalLoading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
        ) : globalSearch ? (
          <button
            type="button"
            onClick={closeGlobalSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label="Effacer la recherche"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}

        {globalResults && (
          <div
            data-testid="global-search-results"
            className="absolute left-0 right-0 top-[46px] z-50 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
          >
            {!globalResults.clients?.length &&
             !globalResults.dossiers?.length &&
             !globalResults.devis?.length ? (
              <div className="px-4 py-5 text-sm text-slate-500">
                Aucun résultat pour « {globalSearch.trim()} »
              </div>
            ) : (
              <div className="max-h-[520px] overflow-y-auto py-2">

                {!!globalResults.clients?.length && (
                  <div>
                    <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                      Clients
                    </div>
                    {globalResults.clients.map((client) => (
                      <button
                        key={`client-${client.id}`}
                        type="button"
                        onClick={() => openGlobalClient(client)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#102A4C] truncate">
                            {client.nom}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {[client.type, client.ville].filter(Boolean).join(" · ") || "Client"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!!globalResults.dossiers?.length && (
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                      Dossiers
                    </div>
                    {globalResults.dossiers.map((dossier) => (
                      <button
                        key={`dossier-${dossier.id}`}
                        type="button"
                        onClick={() => openGlobalDossier(dossier)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center">
                          <FolderOpen className="h-4 w-4 text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#102A4C] truncate">
                            {dossier.numero || "Dossier"} · {dossier.titre || "Sans titre"}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {[dossier.client_nom, dossier.statut].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!!globalResults.devis?.length && (
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                      Devis
                    </div>
                    {globalResults.devis.map((devis) => (
                      <button
                        key={`devis-${devis.id}`}
                        type="button"
                        onClick={() => openGlobalDevis(devis)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                          <FileText className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#102A4C] truncate">
                            {devis.numero || "Devis"} · {devis.titre || "Sans titre"}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {[devis.client_nom, devis.chantier_nom, devis.statut].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 sm:hidden font-head font-bold text-[#102A4C]">Bricomarché</div>

      <button
        data-testid="quick-add-button"
        onClick={() => setQuickOpen(true)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-sm"
      >
        <Zap className="h-4 w-4" /> <span className="hidden sm:inline">Nouveau dossier</span>
      </button>

      <QuickAddDialog
        open={quickOpen}
        onOpenChange={setQuickOpen}
        onCreated={() => window.dispatchEvent(new CustomEvent("crm:data-changed"))}
      />

      {isAdmin && (
        <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-50 border border-slate-200" data-testid="role-switcher">
          <button
            data-testid="role-switch-admin"
            onClick={() => setViewAs("admin")}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              viewAs === "admin" ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Admin
          </button>
          <button
            data-testid="role-switch-commercial"
            onClick={() => setViewAs("commercial")}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              viewAs === "commercial" ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Briefcase className="h-3.5 w-3.5" /> Commercial
          </button>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-menu-trigger"
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
              {initials}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-sm font-semibold leading-tight">{user?.name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{effectiveRole}</div>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white border-slate-200 text-[#102A4C] shadow-lg w-56">
          <DropdownMenuLabel className="text-slate-500">{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[#E5EDF6]" />
          <DropdownMenuItem
            data-testid="logout-button"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
          >
            <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
